// Copy for the NovaBotX landing page. Kept separate from layout so marketing
// edits don't touch the components.

export const navLinks = [
  { href: '#solutions', label: 'Solutions' },
  { href: '#workflow', label: 'How it works' },
  { href: '#features', label: 'Features' },
  { href: '#usecases', label: 'Use cases' },
  { href: '#pricing', label: 'Pricing' },
  { href: '#faq', label: 'FAQ' },
];

export const trustItems = ['Secure', 'Vision-enabled', 'Web-enabled', 'Multi-tenant', 'Enterprise-ready'];

export const problems = [
  'Employees waste time searching files and portals',
  'Knowledge is scattered across documents and websites',
  'Onboarding and training take too long',
  'Compliance answers are hard to verify',
  'Internal support teams are overloaded',
];

export const steps = [
  { title: 'Connect your knowledge', text: 'Upload PDFs, images and text files, or add links to crawl websites and intranets.' },
  { title: 'Train in minutes', text: 'NovaBotX™ reads text, tables, charts, diagrams and scanned pages — no coding required.' },
  { title: 'Ask in plain language', text: 'Teams ask natural-language questions and get smart follow-up suggestions.' },
  { title: 'Get sourced answers', text: 'Every response is source-aware, so people can verify it against the original document.' },
];

export const capabilities = [
  { key: 'training', title: 'Rapid AI training', text: 'Train chatbots from documents, links, and websites in minutes.' },
  { key: 'vision', title: 'Vision-enabled AI', text: 'Understands tables, charts, diagrams, and scanned images.' },
  { key: 'crawl', title: 'Web & link crawling', text: 'Indexes content from websites, intranets, FAQs, and portals.' },
  { key: 'tenant', title: 'Multi-tenant architecture', text: 'Strict tenant isolation with dedicated storage per tenant.' },
  { key: 'rbac', title: 'Role-based access control', text: 'User and Admin roles for governance and compliance.' },
  { key: 'suggest', title: 'Smart question suggestions', text: 'Auto-generated questions for faster insights.' },
  { key: 'brand', title: 'Custom tenant branding', text: 'Branded greetings, system messages, and UI text.' },
  { key: 'security', title: 'Enterprise security', text: '2FA authentication and secure access controls.' },
  { key: 'users', title: 'Email-based user management', text: 'Simple onboarding and user provisioning.' },
];

export const reasons = [
  'Reduce time spent searching documents and websites',
  'Enable instant knowledge access across teams',
  'Create one AI assistant across files and web content',
  'Improve decision-making with AI-driven insights',
  'Enhance compliance with controlled access',
  'Support onboarding, training, and internal support',
  'Eliminate manual document lookups',
];

export const useCases = [
  { key: 'hr', title: 'HR policy assistant', text: 'Leave, benefits and handbook answers on demand.' },
  { key: 'compliance', title: 'Compliance & regulatory', text: 'Traceable answers from manuals and audits.' },
  { key: 'finance', title: 'Finance & accounting', text: 'Reference bot for policies and procedures.' },
  { key: 'it', title: 'IT support & SOPs', text: 'Deflect tickets with step-by-step guides.' },
  { key: 'training', title: 'Training & onboarding', text: 'Get new hires productive from day one.' },
  { key: 'ops', title: 'Operations playbooks', text: 'Delivery and process knowledge in one place.' },
  { key: 'legal', title: 'Legal document Q&A', text: 'Search contracts and policies in plain language.' },
  { key: 'product', title: 'Product documentation', text: 'Answer product questions for teams and customers.' },
  { key: 'intranet', title: 'Intranet & portals', text: 'A conversational front door to your intranet.' },
  { key: 'web', title: 'Website knowledge', text: 'Turn public web content into instant answers.' },
];

export const plans = [
  {
    name: 'Starter',
    desc: 'For small teams and pilots',
    price: '$29',
    period: '/month',
    annual: 'or $299 billed annually',
    features: [
      '1 tenant', 'Up to 5 users', '50 MB document storage', 'PDFs, images, text uploads',
      'Up to 5 website links', 'Vision-enabled understanding', 'Web & link crawling',
      'Smart question suggestions', 'Source-aware answers', 'Email-based user management',
      '2FA authentication', 'Standard email support',
    ],
    ideal: 'Small teams • POCs • Startups',
    cta: { label: 'Get started', to: '/register' },
  },
  {
    name: 'Business',
    desc: 'For growing teams and departments',
    price: '$99',
    period: '/month',
    annual: 'or $999 billed annually',
    featured: true,
    features: [
      'Up to 25 users', '250 MB document storage', 'Unlimited document uploads',
      'Up to 50 website links', 'Faster crawling & indexing', 'Role-based access control',
      'Custom tenant branding', 'Priority email support', 'Admin dashboard & analytics',
      'Department-level deployment',
    ],
    ideal: 'HR • Compliance • IT • Finance',
    cta: { label: 'Start free trial', to: '/register' },
  },
  {
    name: 'Enterprise',
    desc: 'For mission-critical use',
    price: 'Custom',
    features: [
      'Unlimited users', 'Custom storage limits', 'Unlimited website crawling', 'Advanced RBAC',
      'Tenant-level security policies', 'SSO (SAML / OAuth)', 'Audit logs & compliance',
      'API access & integrations', 'Dedicated infrastructure option', 'SLA-backed uptime',
      'Dedicated onboarding',
    ],
    ideal: 'Large & regulated enterprises',
    cta: { label: 'Contact sales', href: '#contact' },
  },
];

// true / false render as check / cross; strings render as-is.
export const comparison = [
  ['Users', 'Up to 5', 'Up to 25', 'Unlimited'],
  ['Storage', '50 MB', '250 MB', 'Custom'],
  ['Vision AI', true, true, true],
  ['Website crawling', '5', '50', 'Unlimited'],
  ['RBAC', false, true, true],
  ['Custom branding', false, true, true],
  ['SSO', false, false, true],
  ['Audit logs', false, false, true],
];

export const faqs = [
  { q: 'What is NovaBotX™?', a: `NovaBotX™ is an enterprise AI assistant platform that converts your organization's internal knowledge — including documents, websites, and databases — into a conversational, intelligent chatbot. It enables employees and customers to ask questions in natural language and receive accurate, context-aware answers instantly.` },
  { q: 'What types of content can NovaBotX™ ingest?', a: `NovaBotX™ can ingest a wide range of content formats, including:

• PDFs and documents (Word, text files)
• Websites and internal portals
• Knowledge bases and SOPs
• HR policies and compliance documents
• Databases and structured content
• Images and scanned documents (via OCR)

This allows organizations to unify knowledge from multiple sources into one AI system.` },
  { q: 'Can NovaBotX™ crawl websites and links?', a: `Yes. NovaBotX™ can securely crawl public or private websites, intranet pages, and documentation portals. It extracts relevant content and converts it into searchable AI knowledge while maintaining context and structure.` },
  { q: 'Does NovaBotX™ support scanned documents and images?', a: `Yes. NovaBotX™ uses advanced Optical Character Recognition (OCR) to read text from scanned documents, images, tables, and diagrams. This ensures that even non-digital content becomes searchable and usable by the AI assistant.` },
  { q: 'How long does it take to train a chatbot?', a: `In most cases, a chatbot can be deployed within minutes after uploading content. Training time depends on:

• Volume of documents
• Complexity of data
• Integration requirements

Enterprise deployments with integrations may take longer, but basic setups are typically very fast.` },
  { q: 'Is NovaBotX™ secure?', a: `Security is a core design principle. NovaBotX™ supports:

• Enterprise-grade encryption
• Role-based access controls
• Tenant data isolation
• Secure API integrations
• Compliance-ready architecture

Your data remains private and is never shared across organizations.` },
  { q: 'Is data isolated between tenants?', a: `Yes. NovaBotX™ uses a multi-tenant architecture with strict data isolation. Each organization’s knowledge, conversations, and configurations are securely separated to prevent cross-access between tenants.` },
  { q: 'Who can upload or manage content?', a: `Administrators and authorized users can upload or manage content based on role permissions. Organizations can define access levels such as:

• Admin
• Manager
• Contributor
• Viewer

This ensures proper governance and control over knowledge assets.` },
  { q: 'Can we customize the chatbot branding?', a: `Yes. NovaBotX™ supports full customization, including:

• Logo and colors
• Chat widget design
• Domain integration
• Custom assistant personality
• UI styling

This allows the chatbot to match your organization's brand identity.` },
  { q: 'Does NovaBotX™ provide source references?', a: `Yes. NovaBotX™ can provide source citations along with answers, allowing users to verify information and trace responses back to original documents or pages. This improves trust, transparency, and compliance.` },
  { q: 'Can NovaBotX™ be used for compliance and regulatory content?', a: `Absolutely. NovaBotX™ is ideal for:
• Compliance manuals
• Regulatory documents
• Audit references
• Legal policies
• Internal governance documentation
It ensures controlled access and traceable answers.` },
  { q: 'What are common use cases?', a: `NovaBotX™ is commonly used for:
• HR policy and employee handbook assistant
• Compliance and regulatory document assistant
• Finance and accounting reference bot
• IT support and SOP knowledge base
• Training and onboarding assistant
• Operations and delivery playbook bot
• Legal document Q&A assistant
• Product documentation and FAQ assistant
• Intranet and portal assistant
• Website knowledge assistant` },
  { q: 'How are users onboarded?', a: `Users are onboarded using:
• Email-based user provisioning
• Role-based access (User/Admin)
• Secure login with 2FA` },
  { q: 'Do we need technical skills to use NovaBotX™?', a: `No. NovaBotX™ is designed for business users and administrators.
No coding or AI expertise is required.` },
  { q: 'How is NovaBotX™ different from ChatGPT or generic chatbots?', a: `NovaBotX™ is enterprise-grade and purpose-built for internal knowledge:
• Secure multi-tenant isolation
• Vision-enabled document understanding
• Website and link crawling
• Role-based governance
• Source-aware answers
• Custom tenant branding
• Enterprise security and compliance` },
  { q: 'How do we get started?', a: `You can get started in minutes:
1. Request a demo
2. Create a tenant
3. Upload documents or add links
4. Train your chatbot
5. Start asking questions` },
  { q: 'Is there a trial or demo available?', a: `Yes.
You can request a live demo or a trial environment to evaluate NovaBotX™ with your own documents and links.` },
  { q: 'What kind of support is available?', a: `NovaBotX™ offers:
• Email support
• Guided onboarding
• Admin training
• Enterprise support (for higher tiers)` },
  { q: 'Can NovaBotX™ scale across departments?', a: `Yes.
NovaBotX™ is designed to scale across teams and departments with:
• Tenant-level isolation
• Role-based governance
• Secure content control` },
  { q: 'Is NovaBotX™ suitable for small teams and large enterprises?', a: `Yes.
NovaBotX™ supports:
• Small teams
• Mid-size organizations
• Large enterprises
With flexible onboarding and deployment models.` },
];

// Scripted conversation for the hero chat preview.
export const demoChat = [
  { from: 'user', text: 'How many days of paid leave do new employees get?' },
  {
    from: 'bot',
    text: 'New full-time employees receive 18 days of paid leave per year, accrued monthly from their start date. Unused days (up to 5) roll over to the next year.',
    source: 'HR Handbook 2026.pdf · p. 14',
  },
  { from: 'user', text: 'And who approves it?' },
  {
    from: 'bot',
    text: 'Your direct manager approves leave requests in the HR portal. Requests over 10 consecutive days also need HR sign-off.',
    source: 'intranet/hr/leave-policy',
  },
];

export const suggestions = ['What’s our remote work policy?', 'How do I submit expenses?', 'Who do I contact for IT?'];
